import React, { useCallback, useEffect, useRef, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import {
    HiOutlineBell, HiOutlineXMark, HiOutlineCheckCircle, HiOutlineUserPlus,
    HiOutlineCreditCard, HiOutlineHeart, HiOutlineShieldCheck, HiOutlineTrash,
    HiOutlineChatBubbleLeftRight, HiOutlineChatBubbleLeft, HiOutlineExclamationTriangle,
    HiOutlineSparkles, HiOutlineStar, HiOutlineEye,
} from "react-icons/hi2"
import API from "../Common/API"

const POLL_MS = 30000  // 30 seconds

const TYPE_CONFIG = {
    new_registration:         { icon: HiOutlineUserPlus,   color: "text-emerald-600", bg: "bg-emerald-50",  border: "border-l-emerald-400" },
    plan_subscribed:          { icon: HiOutlineCreditCard,  color: "text-blue-600",    bg: "bg-blue-50",     border: "border-l-blue-400"    },
    plan_updated:             { icon: HiOutlineCreditCard,  color: "text-violet-600",  bg: "bg-violet-50",   border: "border-l-violet-400"  },
    shortlisted:              { icon: HiOutlineHeart,        color: "text-pink-600",    bg: "bg-pink-50",     border: "border-l-pink-400"    },
    status_change:            { icon: HiOutlineShieldCheck,  color: "text-amber-600",   bg: "bg-amber-50",    border: "border-l-amber-400"   },
    account_deleted:          { icon: HiOutlineTrash,        color: "text-red-600",     bg: "bg-red-50",      border: "border-l-red-400"     },
    otp_request:              { icon: HiOutlineShieldCheck,         color: "text-cyan-600",    bg: "bg-cyan-50",     border: "border-l-cyan-400"    },
    mobile_verified:          { icon: HiOutlineCheckCircle,         color: "text-emerald-600", bg: "bg-emerald-50",  border: "border-l-emerald-400" },
    new_enquiry:              { icon: HiOutlineChatBubbleLeftRight,  color: "text-orange-600",  bg: "bg-orange-50",   border: "border-l-orange-400"  },
    new_message:              { icon: HiOutlineChatBubbleLeft,       color: "text-blue-600",    bg: "bg-blue-50",     border: "border-l-blue-400"    },
    user_reported:            { icon: HiOutlineExclamationTriangle,  color: "text-red-600",     bg: "bg-red-50",      border: "border-l-red-400"     },
    payment_received:         { icon: HiOutlineCreditCard,           color: "text-green-600",   bg: "bg-green-50",    border: "border-l-green-400"   },
    // User-facing notification types
    welcome:                  { icon: HiOutlineSparkles,             color: "text-emerald-600", bg: "bg-emerald-50",  border: "border-l-emerald-400" },
    plan_activated:           { icon: HiOutlineStar,                 color: "text-violet-600",  bg: "bg-violet-50",   border: "border-l-violet-400"  },
    profile_viewed:           { icon: HiOutlineEye,                  color: "text-sky-600",     bg: "bg-sky-50",      border: "border-l-sky-400"     },
}

const getConfig = (type) => TYPE_CONFIG[type] || { icon: HiOutlineBell, color: "text-slate-500", bg: "bg-slate-50", border: "border-l-slate-300" }

const timeAgo = (iso) => {
    if (!iso) return ""
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
    if (diff < 60) return "just now"
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
    return `${Math.floor(diff / 86400)}d ago`
}

/* ─── Bell button (export separately so TopBar/MemberTopBar can use it) ───── */
export const BellButton = ({ unread, onClick, onRefresh }) => (
    <button
        type="button"
        onClick={() => { onRefresh?.(); onClick?.() }}
        className="relative flex h-10 w-10 items-center justify-center rounded-full border border-borderColor bg-white shadow-soft transition hover:border-primeColor hover:text-primeColor"
        title={unread > 0 ? `${unread} unread notification${unread !== 1 ? "s" : ""}` : "Notifications"}
    >
        <HiOutlineBell className="h-5 w-5 text-tColor" />
        {unread > 0 && (
            <motion.span
                key={unread}
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-white"
            >
                {unread > 99 ? "99+" : unread}
            </motion.span>
        )}
    </button>
)

/* ─── Main hook — fetch + poll notifications ─────────────────────────────── */
export const useNotifications = () => {
    const [notifications, setNotifications] = useState([])
    const [unread, setUnread] = useState(0)
    const timerRef = useRef(null)

    const fetch = useCallback(async () => {
        try {
            const res = await API.get("/notifications")
            if (res.status === 200) {
                setNotifications(res.data.data || [])
                setUnread(res.data.unread || 0)
            }
        } catch { /* silent — don't break UI */ }
    }, [])

    useEffect(() => {
        fetch()
        timerRef.current = setInterval(fetch, POLL_MS)
        return () => clearInterval(timerRef.current)
    }, [fetch])

    const markRead = useCallback(async (id) => {
        try { await API.post(`/notifications/markRead/${id}`) } catch {}
        setNotifications((prev) => prev.map((n) => n._id === id ? { ...n, read: true } : n))
        setUnread((u) => Math.max(0, u - 1))
    }, [])

    const markAllRead = useCallback(async () => {
        try { await API.post("/notifications/markAllRead") } catch {}
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
        setUnread(0)
    }, [])

    return { notifications, unread, fetch, markRead, markAllRead }
}

/* ─── Slide-over panel ───────────────────────────────────────────────────── */
const NotificationPanel = ({ open, onClose, notifications, unread, markRead, markAllRead }) => {
    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-[90] flex justify-end"
                    style={{ background: "rgba(0,0,0,0.35)" }}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    <motion.aside
                        className="flex h-full w-full max-w-sm flex-col bg-white shadow-2xl"
                        initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
                        transition={{ type: "spring", damping: 30, stiffness: 280 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between border-b border-borderColor bg-white px-5 py-4">
                            <div className="flex items-center gap-2">
                                <HiOutlineBell className="h-5 w-5 text-primeColor" />
                                <h2 className="text-lg font-semibold text-tColor">Notifications</h2>
                                {unread > 0 && (
                                    <span className="rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold text-white">{unread}</span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {unread > 0 && (
                                    <button
                                        type="button"
                                        onClick={markAllRead}
                                        className="flex items-center gap-1 rounded-full border border-borderColor px-3 py-1.5 text-xs font-bold text-tpColor transition hover:border-primeColor hover:text-primeColor"
                                    >
                                        <HiOutlineCheckCircle className="h-3.5 w-3.5" />
                                        Mark all read
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="flex h-8 w-8 items-center justify-center rounded-full border border-borderColor text-slate-500 transition hover:border-red-300 hover:text-red-500"
                                >
                                    <HiOutlineXMark className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                        {/* List */}
                        <div className="flex-1 overflow-y-auto divide-y divide-borderColor">
                            {notifications.length === 0 ? (
                                <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
                                    <HiOutlineBell className="h-10 w-10 text-slate-200" />
                                    <p className="text-sm font-semibold text-slate-400">No notifications yet</p>
                                    <p className="text-xs text-slate-300">You'll see updates here as they happen</p>
                                </div>
                            ) : (
                                notifications.map((n) => {
                                    const cfg = getConfig(n.type)
                                    const Icon = cfg.icon
                                    return (
                                        <motion.div
                                            key={n._id}
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            className={`relative flex gap-3 border-l-4 px-5 py-4 transition cursor-pointer
                                                ${n.read ? "border-l-transparent bg-white hover:bg-slate-50" : `${cfg.border} ${cfg.bg} hover:brightness-95`}`}
                                            onClick={() => !n.read && markRead(n._id)}
                                        >
                                            <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${n.read ? "bg-slate-100" : cfg.bg}`}>
                                                <Icon className={`h-4 w-4 ${n.read ? "text-slate-400" : cfg.color}`} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className={`text-sm font-semibold ${n.read ? "text-slate-500" : "text-tColor"}`}>
                                                    {n.title}
                                                </p>
                                                <p className="mt-0.5 text-xs leading-5 text-tpColor">{n.message}</p>
                                                <p className="mt-1 text-[10px] text-slate-400">{timeAgo(n.createdAt)}</p>
                                            </div>
                                            {!n.read && (
                                                <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primeColor" />
                                            )}
                                        </motion.div>
                                    )
                                })
                            )}
                        </div>
                    </motion.aside>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

export default NotificationPanel
